import {
  CLIENT_PRODUCTS_REFUSALS,
  ClientProducts,
  ClientProductsRefusal,
  ClientProductsValue,
} from "@/data/models/ClientAdministrationRow";

/**
 * The Produkte card's logic (#2610089 stage 1). Pure. IRES Cloud's `setClientProductsForAdministration`
 * is the authority; these rules decide what the dialog shows and sends, and derive the old product
 * choice (`projectManagement`) the same way IRES Cloud does, for the read-only "Produktwahl (alt)".
 */

/**
 * The four values the old `projectManagement` choice can take once derived from the switches. Exactly
 * the `ProjectManagement` enum's string values (`@/data/models/Client`), kept as literals here so this
 * light module does not pull that heavy model (and the vuetify chain behind it) into the pure specs.
 */
export type DerivedProductChoice = "DEVICES_ONLY_WEBSITE" | "WEBSITE" | "DEFAULT" | "PROJECT_OPTION";

/** The four product switches, in the order the card and the dialog show them. */
export const PRODUCT_SWITCHES: Array<keyof ClientProductsValue> = [
  "officeDeviceAssignment",
  "project",
  "work",
  "interface",
];

/** The one derived product choice per combination of switches. */
export const PRODUCT_CHOICES: DerivedProductChoice[] = ["DEVICES_ONLY_WEBSITE", "WEBSITE", "DEFAULT", "PROJECT_OPTION"];

const PRODUCT_CHOICE_LABEL_KEYS: Record<DerivedProductChoice, string> = {
  DEVICES_ONLY_WEBSITE: "admin.clients.products.choices.DEVICES_ONLY_WEBSITE",
  WEBSITE: "admin.clients.products.choices.WEBSITE",
  DEFAULT: "admin.clients.products.choices.DEFAULT",
  PROJECT_OPTION: "admin.clients.products.choices.PROJECT_OPTION",
};

/**
 * The "Produktwahl (alt)" the switches derive, by the same rule IRES Cloud writes `projectManagement`
 * with: IRES Project with GZ / GL in IRES Office is Projektoption, IRES Project alone Projektmanager,
 * GZ / GL or IRES Work Projektportal, nothing Geräteportal.
 */
export function derivedProductChoice(value: ClientProductsValue): DerivedProductChoice {
  if (value.project && value.officeDeviceAssignment) {
    return "PROJECT_OPTION";
  }
  if (value.project) {
    return "DEFAULT";
  }
  if (value.officeDeviceAssignment || value.work) {
    return "WEBSITE";
  }
  return "DEVICES_ONLY_WEBSITE";
}

/** The i18n key for a product choice's label (Android's: Geräteportal / Projektportal / …); null for any other value. */
export function productChoiceLabelKey(choice: string): string | null {
  return PRODUCT_CHOICE_LABEL_KEYS[choice as DerivedProductChoice] ?? null;
}

/** The switches as the dialog edits them and IRES Cloud takes them, without the `derived` marker. */
export function toProductsValue(products: ClientProducts): ClientProductsValue {
  return {
    officeDeviceAssignment: products.officeDeviceAssignment,
    project: products.project,
    work: products.work,
    interface: products.interface,
  };
}

/** Whether two sets of switches differ. */
export function productsDiffer(a: ClientProductsValue, b: ClientProductsValue): boolean {
  return PRODUCT_SWITCHES.some((key) => a[key] !== b[key]);
}

/** The dialog may save when something changed. */
export function canSaveProducts(stored: ClientProductsValue, form: ClientProductsValue): boolean {
  return productsDiffer(stored, form);
}

/**
 * Whether to show the "what a save reaches" warning: only once something changed, since an unchanged
 * dialog reaches nothing (ui-conventions.md: an action that reaches beyond this screen names it).
 */
export function showProductsSaveWarning(stored: ClientProductsValue, form: ClientProductsValue): boolean {
  return productsDiffer(stored, form);
}

/** IRES Cloud's refusal reason in a failed call, else null. */
export function clientProductsRefusalOf(error: unknown): ClientProductsRefusal | null {
  const reason = (error as { details?: { reason?: unknown } } | null)?.details?.reason;
  return typeof reason === "string" && CLIENT_PRODUCTS_REFUSALS.includes(reason as ClientProductsRefusal)
    ? (reason as ClientProductsRefusal)
    : null;
}

/** The i18n key of a refusal from IRES Cloud, by its `details.reason`; a general failure otherwise. */
export function clientProductsErrorKey(error: unknown): string {
  const reason = clientProductsRefusalOf(error);
  return reason ? `admin.clients.products.errors.${reason}` : "admin.clients.products.errors.FAILED";
}
